import type { ReactNode } from "react";
import { SkyButton, SkyText } from "@skypointcloud/skypoint-ui";
import { Link } from "wouter";
import { routeLabel, type DeliveryRow } from "@shared/types/notifications";
import { LoadingRows } from "@/components/access/ScreenStates";
import { failureMessage } from "@/lib/api";
import { RetryIcon } from "@/lib/icons";
import {
  deliveryChannel,
  deliveryStatusSentence,
  deliveryWhat,
  deliveryWhen,
} from "@/lib/notifications/sentences";
import { absolutePath } from "@/lib/routes";
import { formatInstant, instantHoverTitle, zoneCaption } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Recent deliveries (contract-notif R10, D10): the newest 20 delivery rows of this instance,
 * every status, held-back ones included. The deliveries table is the record of what was sent
 * (no audit row per delivery), and this grid is where a person reads it.
 *
 * A plain table with a caption (R14), as `ModelHistoryTimeline` does: the library grid has no
 * caption. Addresses are always masked here and L1 targets read "L1 team" (§6.5); the outcome is
 * a word, never only a colour. It borrows the library grid's look (header weight and padding,
 * cell padding, row divider, token hover, no frame) so it reads as one of the app's grids
 * (rules.md §4; `__tests__/grids.conventions.test.tsx`).
 */
/**
 * The contract's seven headings and alert routing's "Why". The time and the run's outcome were
 * both headed "When", told apart only for a screen reader (review r1 N1); the visual check of
 * build 1833938 (2026-09-30) found that confusing on screen too, so each now says what it holds:
 * "Time" (when the delivery was queued; "Sent" would be untrue of a held or failed row) and
 * "Outcome" (Failure, Warning, Success or Recovered, the rule's "When").
 */
const HEADERS = [
  "Time",
  "Run",
  "What",
  "Outcome",
  "Channel",
  "To",
  // Alert routing (2026-09-28): a cell's rule, a rule for chosen targets (named), or a Watch.
  "Why",
  "Status",
] as const;

export function DeliveriesTable({
  rows,
  isLoading,
  error,
  instanceName,
  hasRules,
  onRetry,
}: {
  rows: DeliveryRow[] | undefined;
  isLoading: boolean;
  error: unknown;
  instanceName: string;
  /** False while the instance has no rule: nothing will be sent until one is added. */
  hasRules: boolean;
  onRetry: () => void;
}) {
  const zone = useDisplayTz();
  function renderDeliveries(): ReactNode {
    if (isLoading) {
      return (
        <div data-testid="deliveries-loading">
          <LoadingRows rows={3} columns={8} />
        </div>
      );
    }
    if (error) {
      return (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3"
          data-testid="deliveries-error"
        >
          <SkyText size={300}>Recent deliveries did not load. {failureMessage(error, "")}</SkyText>
          <SkyButton appearance="secondary" size="small" icon={RetryIcon} onClick={onRetry}>
            Retry
          </SkyButton>
        </div>
      );
    }
    if (!rows || rows.length === 0) {
      return (
        <SkyText size={300} color="muted" block testId="deliveries-empty">
          {hasRules
            ? "Nothing has been sent for this instance yet. The next run that finishes sends according to the rules above."
            : "Nothing has been sent for this instance yet. Add a rule above and the next run that finishes will send according to it."}
        </SkyText>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse bg-background text-left"
          data-testid="deliveries-grid"
        >
          <caption className="px-3 py-2 text-left">
            <SkyText size={200} color="muted">
              Recent deliveries for {instanceName}. {zoneCaption(zone)}.
            </SkyText>
          </caption>
          <thead>
            <tr>
              {HEADERS.map((header) => (
                <th
                  key={header}
                  scope="col"
                  className="whitespace-nowrap border-b border-border px-4 py-3 text-sm font-semibold text-foreground-soft"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-border hover:bg-muted/50"
                data-testid="delivery-row"
                data-status={row.status}
              >
                <td className="px-3 py-2">
                  <SkyText size={200} font="numeric" title={instantHoverTitle(row.createdAt, zone)}>
                    {formatInstant(row.createdAt, zone, "minutes")}
                  </SkyText>
                </td>
                <td className="px-3 py-2">
                  {row.eventKind === "run" && row.runId ? (
                    <Link
                      href={absolutePath(`/runs/${row.runId}`)}
                      className="text-sm tabular-nums underline underline-offset-2"
                    >
                      {row.runId}
                    </Link>
                  ) : (
                    <SkyText size={200}>
                      {row.eventKind === "instance_test" || row.eventKind === "email_test"
                        ? "Test"
                        : "—"}
                    </SkyText>
                  )}
                </td>
                <td className="px-3 py-2">
                  <SkyText size={200}>{deliveryWhat(row)}</SkyText>
                </td>
                <td className="px-3 py-2">
                  <SkyText size={200}>{deliveryWhen(row)}</SkyText>
                </td>
                <td className="px-3 py-2">
                  <SkyText size={200}>{deliveryChannel(row.channel)}</SkyText>
                </td>
                <td className="px-3 py-2">
                  <SkyText size={200}>{row.target.label}</SkyText>
                </td>
                <td className="px-3 py-2">
                  <SkyText size={200} testId="delivery-why">
                    {routeLabel(row.route, row.routeRuleName)}
                  </SkyText>
                </td>
                <td className="px-3 py-2">
                  <SkyText size={200} testId="delivery-status">
                    {deliveryStatusSentence(row, zone)}
                  </SkyText>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section aria-labelledby="recent-deliveries-heading" className="flex flex-col gap-2">
      <SkyText as="h3" size={300} weight="semibold" block id="recent-deliveries-heading">
        Recent deliveries
      </SkyText>
      {renderDeliveries()}
    </section>
  );
}
